import type {
    RefInfo,
    ResolvedReference,
    SchemaFile,
    CoreSchemaConfig,
    WixReferenceAnalysis
} from '@wix/document-services-types'
import _ from 'lodash'
import ajv, {Ajv, ErrorObject, Options} from 'ajv'
import draftSchema from './draftSchema.json'
import {SchemaValidationError, CannotFindSchemaError, SchemaValidationErrorDetails} from './errors'
import {combineAllOf, dereference} from './schemaUtils'
import * as referenceUtil from './referenceUtil'

export type ValidationFunction = (namespace: string, dataTypeName: string, data: any) => void

const convertAjvError = (error: ErrorObject): SchemaValidationErrorDetails => {
    return _.pick(error, ['message', 'dataPath', 'keyword', 'schemaPath', 'params']) as SchemaValidationErrorDetails
}

const convertAjvErrors = (errors: ErrorObject[]): SchemaValidationError =>
    new SchemaValidationError(_.map(errors, convertAjvError))

interface CoreValidators {
    withDefaults: Ajv
    validation: Ajv
    removing?: Ajv
    strict?: Ajv
}

const keyByPath = (refInfos: RefInfo[]) => _.keyBy(refInfos, info => info.path.join('.'))
const createSchemaCore = (coreSchemaConfig: CoreSchemaConfig, referencesMap: WixReferenceAnalysis) => {
    const {namespaces, restrictedSchemas, permanentDataTypes, formats} = coreSchemaConfig
    const options: Options = {useDefaults: false, extendRefs: true, strictNumbers: true, formats}
    const validators = {
        withDefaults: ajv({...options, useDefaults: true}),
        validation: ajv(options)
    } as CoreValidators
    if (restrictedSchemas) {
        validators.removing = ajv({...options, removeAdditional: 'failing'})
        validators.strict = ajv(options)
    }

    function registerSchema(schema: SchemaFile, id: string) {
        _.forOwn(validators, validator => {
            validator!.addSchema(schema, id)
        })
    }

    function addInitialSchema(schema: SchemaFile, id: string, namespace: string) {
        _.forOwn(validators, (validator, validatorName) => {
            const schemaToRegister =
                (['removing', 'strict'].includes(validatorName) ? restrictedSchemas![namespace] : schema) ?? schema
            validator!.addSchema(schemaToRegister, id)
        })
    }

    _.forOwn(namespaces, (schema: SchemaFile, name: string) => {
        addInitialSchema(schema, schema.$id ?? name, name)
    })

    function removeSchema(id: string) {
        _.forOwn(validators, validator => {
            validator!.removeSchema(id)
        })
    }

    function getValidationKey(namespace: string, dataTypeName: string): string {
        const ns = namespaces[namespace]
        return `${ns.$id}#/${dataTypeName}`
    }

    function validateWithCustomValidator(validator: ajv.Ajv, namespace: string, dataTypeName: string, data: any): void {
        if (!(namespace in namespaces)) {
            throw new CannotFindSchemaError(namespace, dataTypeName)
        }
        const key = getValidationKey(namespace, dataTypeName)
        const validationFunction = validator.getSchema(key)
        const valid = validationFunction?.(data)
        if (!valid) {
            throwValidationErrors(validationFunction?.errors)
        }
    }

    // mutate
    const addDefaultsAndValidate: ValidationFunction = (namespace: string, dataTypeName: string, data: any) => {
        validateWithCustomValidator(validators.withDefaults, namespace, dataTypeName, data)
    }

    const validateStrict: ValidationFunction = (namespace: string, dataTypeName: string, data: any) => {
        const dataToValidate = _.cloneDeep(data)
        validateWithCustomValidator(
            validators.strict ?? validators.withDefaults,
            namespace,
            dataTypeName,
            dataToValidate
        )
    }

    const validateNoDefaults: ValidationFunction = (namespace: string, dataTypeName: string, data: any) => {
        validateWithCustomValidator(validators.validation, namespace, dataTypeName, data)
    }

    function removeAdditionalProperties(namespace: string, dataTypeName: string, data: any): void {
        if (validators.removing) {
            validateWithCustomValidator(validators.removing, namespace, dataTypeName, data)
        }
    }

    function throwValidationErrors(errors?: ErrorObject[] | null) {
        throw convertAjvErrors(errors ?? [])
    }

    function hasNamespace(namespace: string) {
        return !!_.get(namespaces, [namespace])
    }

    function hasSchemaForDataType(namespace: string, dataTypeName: string) {
        return !!_.get(namespaces, [namespace, dataTypeName])
    }

    // This is a workaround to remove internal cache created by AJV fragments behaviour
    // We get to this flow only in "Schema Dev Mode"
    // See - https://github.com/ajv-validator/ajv/issues/1293
    function clearAjvFragmentCacheForSchema(namespace: string, schema: SchemaFile, schemaName: string) {
        const key = getValidationKey(namespace, schemaName)
        removeSchema(`#/${schemaName}`)
        removeSchema(key)
        registerSchema(schema, key)
        registerSchema(schema, `#/${schemaName}`)
    }

    function extendDraftSchema(schema: any) {
        if (schema.isDraftSchema) {
            if (schema.allOf) {
                return {
                    isDraftSchema: true,
                    ...schema,
                    allOf: [draftSchema, ...schema.allOf]
                }
            }

            if (schema.properties) {
                return {
                    ..._.omit(schema, ['properties']),
                    isDraftSchema: true,
                    properties: _.merge(schema.properties, draftSchema.properties),
                    required: _.union(schema.required || [], draftSchema.required)
                }
            }
            return {
                ...schema,
                isDraftSchema: true,
                properties: draftSchema.properties,
                required: _.union(schema.required || [], draftSchema.required)
            }
        }
        return schema
    }

    function extendSchemaAndCombineAllOf(ns: any, schema: any, name: string) {
        const extended = extendDraftSchema(schema)
        const dereferenced = dereference(extended, {ns, common: namespaces.common, name})
        return combineAllOf(dereferenced)
    }

    const REFERENCE_TYPES = ['ref', 'weakRef', 'refList']

    function isReference(val: any) {
        return _.intersection(REFERENCE_TYPES, val?.pseudoType).length > 0
    }

    function extractRefsFromSchema(namespace: string, schema: any, result: RefInfo[], parentPath: string[]) {
        const subSchemas = schema.allOf || schema.anyOf || schema.oneOf || []
        _.forEach(subSchemas, subSchema => extractRefsFromSchema(namespace, subSchema, result, parentPath))
        if (schema.properties) {
            const {$ref} = schema.properties
            const currentSchema = $ref ? namespaces[namespace][$ref] : schema
            _.forOwn(currentSchema.properties, (val, key) => {
                if (val?.$ref) {
                    val = namespaces[namespace][val.$ref]
                }
                if (isReference(val)) {
                    const path = parentPath.concat(key)
                    const isWeakRef = val?.pseudoType.includes('weakRef')
                    const isList = val?.pseudoType.includes('refList')
                    result.push({
                        path,
                        isList,
                        jsonPointer: `/${path.join('/')}`,
                        shouldCollect: !isWeakRef,
                        isRefOwner: !isWeakRef,
                        shouldValidate: !isWeakRef,
                        referencedMap: namespace,
                        refTypes: []
                    })
                } else if (val.type === 'object' && val.properties) {
                    extractRefsFromSchema(namespace, val, result, parentPath.concat(key))
                }
            })
        }
    }

    function addReferences(namespace: string, name: string) {
        const ns = namespaces[namespace]
        const schema = ns[name]
        const refs: RefInfo[] = []
        extractRefsFromSchema(namespace, schema, refs, [])
        if (refs.length) {
            _.setWith(referencesMap, [namespace, name], keyByPath(refs), Object)
        }
    }

    function addDataTypesToExistingNamespace(
        namespace: string,
        schemas: Record<string, SchemaFile>,
        override: boolean = false
    ) {
        const ns = namespaces[namespace]
        const id = ns.$id
        removeSchema(id)
        _.forEach(schemas, (schema, name) => {
            const extended = extendSchemaAndCombineAllOf(ns, schema, name)
            ns[name] = extended
            if (restrictedSchemas) {
                restrictedSchemas[namespace][name] = extended
            }
            if (override) {
                clearAjvFragmentCacheForSchema(namespace, ns[name] as unknown as SchemaFile, name)
            }
            addReferences(namespace, name)
        })
        addInitialSchema(ns, id, namespace)
    }

    function registerNamespace(name: string, schemas: SchemaFile) {
        if (!namespaces[name]) {
            const ns = schemas.$id ? schemas : {...schemas, $id: name}
            namespaces[name] = ns
            addInitialSchema(ns, ns.$id, name)
        }
    }

    const isDraftDataSchema = (namespace: string, dataTypeName: string) =>
        _.get(namespaces, [namespace, dataTypeName, 'isDraftSchema'], false)
    const isDraftItem = (dataItem: any) => !!dataItem?._isDraftItem

    function extractReferences(namespace: string, dataTypeName: string, data: any): readonly ResolvedReference[] {
        return referenceUtil.extractReferences(referencesMap, namespace, dataTypeName, data)
    }

    //this function extracts the fields from the data item which are 'reference' fields according to the schema
    function extractReferenceFieldsInfo(namespace: string, dataTypeName: string, data?: any): readonly RefInfo[] {
        const refs = _.get(referencesMap, [namespace, dataTypeName])
        if (data) {
            return _.filter(refs, (refInfo: RefInfo) => _.has(data, refInfo.path))
        }
        return _.values(refs)
    }

    function extractOwnedReferenceFieldsInfo(
        namespace: string,
        dataTypeName: string,
        onlySerializable?: boolean,
        data?: any
    ): Record<string, RefInfo> {
        const refs = _.get(referencesMap, [namespace, dataTypeName])
        return _.pickBy(
            refs,
            refInfo =>
                refInfo.isRefOwner &&
                (!data || _.has(data, refInfo.path)) &&
                (!onlySerializable || !refInfo.dontSerialize)
        )
    }

    const isPermanentDataType = (namespace: string, type: string): boolean =>
        _.get(permanentDataTypes, [namespace, type], false)

    return {
        addDefaultsAndValidate,
        validate: validateNoDefaults,
        validateStrict,
        validateNoDefaults,
        hasNamespace,
        hasSchemaForDataType,
        isPermanentDataType,
        registerNamespace,
        addDataTypesToExistingNamespace,
        isDraftDataSchema,
        isDraftItem,
        extractReferenceFieldsInfo,
        extractOwnedReferenceFieldsInfo,
        extractReferences,
        removeAdditionalProperties
    }
}

export {createSchemaCore}
